import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Globe, Search, User, Mail, Phone, Calendar, Users as UsersIcon, Loader2, Eye, Trash2, Ban, CheckCircle, MoreHorizontal } from "lucide-react";
import AdminPageBackground from "@/components/admin/AdminPageBackground";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import UserDetailsDialog from "@/components/admin/UserDetailsDialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

interface Visitor {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  created_at: string;
  user_id: string | null;
  status: string;
}

const Users = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  const [users, setUsers] = useState<Visitor[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<Visitor[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedUser, setSelectedUser] = useState<Visitor | null>(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<Visitor | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    if (searchTerm.trim() === "") {
      setFilteredUsers(users);
    } else {
      const filtered = users.filter(
        (user) =>
          user.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
          (user.phone && user.phone.includes(searchTerm))
      );
      setFilteredUsers(filtered);
    }
  }, [searchTerm, users]);

  const fetchUsers = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("visitors")
      .select("*")
      .order("created_at", { ascending: false });

    if (error) {
      console.error("Error fetching users:", error);
    } else {
      setUsers(data || []);
      setFilteredUsers(data || []);
    }
    setLoading(false);
  };

  const formatDate = (dateString: string) => {
    return format(new Date(dateString), "dd MMM yyyy, HH:mm", {
      locale: isRTL ? ar : enUS,
    });
  };

  const handleToggleStatus = async (user: Visitor) => {
    setActionLoading(true);
    const newStatus = user.status === 'active' ? 'suspended' : 'active';
    
    const { error } = await supabase
      .from("visitors")
      .update({ status: newStatus })
      .eq("id", user.id);

    if (error) {
      toast.error(isRTL ? "حدث خطأ أثناء تحديث الحالة" : "Error updating status");
      console.error("Error updating user status:", error);
    } else {
      toast.success(
        newStatus === 'suspended' 
          ? (isRTL ? "تم إيقاف العضوية بنجاح" : "Membership suspended successfully")
          : (isRTL ? "تم تفعيل العضوية بنجاح" : "Membership activated successfully")
      );
      fetchUsers();
    }
    setActionLoading(false);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    
    setActionLoading(true);
    const { error } = await supabase
      .from("visitors")
      .delete()
      .eq("id", userToDelete.id);

    if (error) {
      toast.error(isRTL ? "حدث خطأ أثناء حذف المستخدم" : "Error deleting user");
      console.error("Error deleting user:", error);
    } else {
      toast.success(isRTL ? "تم حذف المستخدم بنجاح" : "User deleted successfully");
      fetchUsers();
    }
    setDeleteDialogOpen(false);
    setUserToDelete(null);
    setActionLoading(false);
  };

  return (
    <AdminPageBackground className={isRTL ? "rtl" : "ltr"}>
      <nav className="border-b border-white/10 bg-white/5 backdrop-blur-sm">
        <div className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <Link
              to="/admin"
              className="flex items-center gap-2 text-white/70 hover:text-white transition-colors"
            >
              <ArrowLeft className="w-5 h-5" />
              {isRTL ? "العودة للوحة التحكم" : "Back to Dashboard"}
            </Link>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => i18n.changeLanguage(i18n.language === "en" ? "ar" : "en")}
              className="rounded-full text-white/70 hover:text-white hover:bg-white/10"
            >
              <Globe className="w-5 h-5" />
            </Button>
          </div>
        </div>
      </nav>

      <main className="max-w-[2000px] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3 text-white">
              <UsersIcon className="w-8 h-8 text-primary" />
              {isRTL ? "إدارة المستخدمين" : "Users Management"}
            </h1>
            <p className="text-white/50 mt-1">
              {isRTL 
                ? `إجمالي المستخدمين المسجلين: ${users.length}`
                : `Total registered users: ${users.length}`}
            </p>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="border-0 bg-white/10 backdrop-blur-md border-white/15">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500/20 flex items-center justify-center">
                  <UsersIcon className="w-6 h-6 text-blue-400" />
                </div>
                <div>
                  <p className="text-sm text-white/50">{isRTL ? "إجمالي المستخدمين" : "Total Users"}</p>
                  <p className="text-2xl font-bold text-white">{users.length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white/10 backdrop-blur-md border-white/15">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                  <Mail className="w-6 h-6 text-green-400" />
                </div>
                <div>
                  <p className="text-sm text-white/50">{isRTL ? "بالبريد الإلكتروني" : "With Email"}</p>
                  <p className="text-2xl font-bold text-white">{users.filter(u => u.email).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-0 bg-white/10 backdrop-blur-md border-white/15">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center">
                  <Phone className="w-6 h-6 text-purple-400" />
                </div>
                <div>
                  <p className="text-sm text-white/50">{isRTL ? "برقم الهاتف" : "With Phone"}</p>
                  <p className="text-2xl font-bold text-white">{users.filter(u => u.phone).length}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className={`absolute ${isRTL ? 'right-3' : 'left-3'} top-1/2 transform -translate-y-1/2 text-white/40 w-5 h-5`} />
            <Input
              type="search"
              placeholder={isRTL ? "البحث بالاسم أو البريد أو الهاتف..." : "Search by name, email or phone..."}
              className={`${isRTL ? "pr-10 pl-4" : "pl-10 pr-4"} bg-white/10 border-white/15 text-white placeholder:text-white/40`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Users Table */}
        <Card className="border-0 bg-white/10 backdrop-blur-md border-white/15">
          <CardHeader>
            <CardTitle className="text-white">{isRTL ? "قائمة المستخدمين" : "Users List"}</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12 text-white/50">
                <User className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>
                  {searchTerm 
                    ? (isRTL ? "لا توجد نتائج للبحث" : "No search results found")
                    : (isRTL ? "لا يوجد مستخدمين مسجلين بعد" : "No registered users yet")}
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-white/10 hover:bg-white/5">
                      <TableHead className={`${isRTL ? "text-right" : "text-left"} text-white/70`}>
                        {isRTL ? "الاسم" : "Name"}
                      </TableHead>
                      <TableHead className={`${isRTL ? "text-right" : "text-left"} text-white/70`}>
                        {isRTL ? "البريد الإلكتروني" : "Email"}
                      </TableHead>
                      <TableHead className={`${isRTL ? "text-right" : "text-left"} text-white/70`}>
                        {isRTL ? "رقم الهاتف" : "Phone"}
                      </TableHead>
                      <TableHead className={`${isRTL ? "text-right" : "text-left"} text-white/70`}>
                        {isRTL ? "تاريخ التسجيل" : "Registration Date"}
                      </TableHead>
                      <TableHead className={`${isRTL ? "text-right" : "text-left"} text-white/70`}>
                        {isRTL ? "الحالة" : "Status"}
                      </TableHead>
                      <TableHead className={`${isRTL ? "text-right" : "text-left"} text-white/70`}>
                        {isRTL ? "الإجراءات" : "Actions"}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredUsers.map((user) => (
                      <TableRow key={user.id} className="border-white/10 hover:bg-white/5">
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                              <User className="w-5 h-5 text-primary" />
                            </div>
                            <span className="font-medium text-white">{user.full_name}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-white/60">
                            <Mail className="w-4 h-4" />
                            {user.email}
                          </div>
                        </TableCell>
                        <TableCell>
                          {user.phone ? (
                            <div className="flex items-center gap-2 text-white/60">
                              <Phone className="w-4 h-4" />
                              {user.phone}
                            </div>
                          ) : (
                            <span className="text-white/30">-</span>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2 text-white/60">
                            <Calendar className="w-4 h-4" />
                            {formatDate(user.created_at)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge 
                            variant="outline" 
                            className={user.status === 'suspended' 
                              ? "bg-red-500/20 text-red-300 border-red-500/30" 
                              : "bg-green-500/20 text-green-300 border-green-500/30"
                            }
                          >
                            {user.status === 'suspended' 
                              ? (isRTL ? "موقوف" : "Suspended")
                              : (isRTL ? "نشط" : "Active")
                            }
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-white/70 hover:text-white hover:bg-white/10">
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align={isRTL ? "start" : "end"}>
                              <DropdownMenuItem
                                onClick={() => {
                                  setSelectedUser(user);
                                  setDetailsOpen(true);
                                }}
                              >
                                <Eye className="w-4 h-4 ml-2" />
                                {isRTL ? "عرض التفاصيل" : "View Details"}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleToggleStatus(user)}
                                disabled={actionLoading}
                              >
                                {user.status === 'suspended' ? (
                                  <>
                                    <CheckCircle className="w-4 h-4 ml-2 text-green-600" />
                                    {isRTL ? "تفعيل العضوية" : "Activate"}
                                  </>
                                ) : (
                                  <>
                                    <Ban className="w-4 h-4 ml-2 text-orange-600" />
                                    {isRTL ? "إيقاف العضوية" : "Suspend"}
                                  </>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => {
                                  setUserToDelete(user);
                                  setDeleteDialogOpen(true);
                                }}
                                className="text-red-600 focus:text-red-600"
                              >
                                <Trash2 className="w-4 h-4 ml-2" />
                                {isRTL ? "حذف المستخدم" : "Delete User"}
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        <UserDetailsDialog
          user={selectedUser}
          open={detailsOpen}
          onOpenChange={setDetailsOpen}
        />

        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>
                {isRTL ? "تأكيد حذف المستخدم" : "Confirm Delete User"}
              </AlertDialogTitle>
              <AlertDialogDescription>
                {isRTL 
                  ? `هل أنت متأكد من حذف المستخدم "${userToDelete?.full_name}"؟ لا يمكن التراجع عن هذا الإجراء.`
                  : `Are you sure you want to delete "${userToDelete?.full_name}"? This action cannot be undone.`
                }
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter className={isRTL ? "flex-row-reverse gap-2" : ""}>
              <AlertDialogCancel disabled={actionLoading}>
                {isRTL ? "إلغاء" : "Cancel"}
              </AlertDialogCancel>
              <AlertDialogAction
                onClick={handleDeleteUser}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700"
              >
                {actionLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  isRTL ? "حذف" : "Delete"
                )}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </main>
    </AdminPageBackground>
  );
};

export default Users;
